<script setup>
import { computed, defineAsyncComponent, onMounted, onUnmounted, ref } from 'vue'
import { usePage } from '@inertiajs/vue3'
import { Clapperboard, Share2, Users } from 'lucide-vue-next'
import CastingCard from '@/Components/Casting/CastingCard.vue'
import HeaderV2 from '@/Components/Reels/HeaderV2.vue'
import BuyStarsModal from '@/Components/Reels/BuyStarsModal.vue'
import {trackEvent, trackFathomEvent} from '@/Helpers/analytics'
import { openStarPackPurchaseUrl } from '@/Helpers/starPackPurchase'
import ProducerJoinModal from "@/Components/Common/ProducerJoinModal.vue";
const SharePopup     = defineAsyncComponent(() => import('@/Components/Common/SharePopup.vue'))

const props = defineProps({
    profile:                   { type: Object,  required: true },
    requestedCastings:         { type: Array,   default: () => [] },
    draftCastings:             { type: Array,   default: () => [] },
    supportedCastings:         { type: Array,   default: () => [] },
    totalStarsGivenToCastings: { type: Number,  default: 0 },
    isOwner:                   { type: Boolean, default: false },
    starPacks:                 { type: Array,   default: () => [] },
})

const { props: sharedProps } = usePage()
const isUserLoggedIn = computed(() => !!(sharedProps.auth?.user?.id))
const isGoldUser     = computed(() => !!(sharedProps.auth?.is_gold))
const routePrefix    = computed(() => sharedProps.routePrefix || 'www')
const requiresAgeVerification = computed(() => !!(sharedProps.requiresAgeVerification))

const handleBalanceUpdate = (newBalance) => {
    if (sharedProps.auth?.user) {
        sharedProps.auth.user.wallet_balance = newBalance
    }
}

const STAR_ICON_URL = 'https://sm.naughtycdn.com/images/landers/prize/NA-Star.png'

const isBuyStarsModalOpen = ref(false)
const isProducerJoinModalOpen = ref(false)
const producerJoinModalContext = ref('pitch')

// ── Guest sticky CTA ─────────────────────────────────────────────────────
const heroRef      = ref(null)
const showGuestCta = ref(false)
let   heroObserver = null

const joinDefaultSource = ref('Public_Castings_Profile')
const joinSfwSource = ref('')

onMounted(() => {
    document.body.classList.add('my-page-body')
    document.body.style.overflow = ''
    document.documentElement.style.overflow = ''

    if (requiresAgeVerification.value) {
        isProducerJoinModalOpen.value = true
        joinSfwSource.value = 'SFW_Public_Castings_Profile'

        trackFathomEvent('SFW Public Castings Profile Loaded')
        trackEvent('SFW Public Castings Profile Loaded')
    }

    if (!isUserLoggedIn.value && heroRef.value) {
        heroObserver = new IntersectionObserver(
            ([entry]) => { showGuestCta.value = !entry.isIntersecting },
            { threshold: 0 }
        )
        heroObserver.observe(heroRef.value)
    }
})

onUnmounted(() => {
    document.body.classList.remove('my-page-body')
    heroObserver?.disconnect()
})

const handleJoinClick = (trigger, context = 'pitch') => {
    isProducerJoinModalOpen.value = true
    producerJoinModalContext.value = context
    trackFathomEvent(trigger || 'CastingsPage Join Clicked')
}

const handleBuyStarPack = (pack) => {
    openStarPackPurchaseUrl(pack, 'castings_hub')
}

// ── Helpers ──────────────────────────────────────────────────────────────
const formatNumber = (n) => n ? Number(n).toLocaleString('en-US') : '0'

const timeAgo = (dateStr) => {
    if (!dateStr) return ''
    const diff = Math.floor((Date.now() - new Date(dateStr)) / 1000)
    if (diff < 60)      return 'just now'
    if (diff < 3600)    return `${Math.floor(diff / 60)}m ago`
    if (diff < 86400)   return `${Math.floor(diff / 3600)}h ago`
    if (diff < 2592000) return `${Math.floor(diff / 86400)}d ago`
    return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
}


const totalStarsReceivedOnRequests = computed(() =>
    props.requestedCastings.reduce((sum, c) => sum + (c.stars_received || 0), 0)
)

const openShare = () => {
    document.getElementById('ShareLinkModal')?.classList.remove('hidden')
}

const handleProducerJoinModalClose = () => {
    isProducerJoinModalOpen.value = false
    joinSfwSource.value = ''
}
</script>

<template>
    <div class="main-container">
        <div class="min-h-screen bg-zinc-950 pb-24">

            <!-- ── HEADER ─────────────────────────────────────────── -->
            <HeaderV2
                :isUserLoggedIn="isUserLoggedIn"
                :isGoldUser="isGoldUser"
                :routePrefix="routePrefix"
                :requiresAgeVerification="requiresAgeVerification"
                :hide="{ videos: !isUserLoggedIn, getStars: !isUserLoggedIn, kebab: !isUserLoggedIn }"
                :promoteFreeJoin="false"
                @joinClick="handleJoinClick"
                @goldJoinClick="isBuyStarsModalOpen = true"
            />

            <!-- ── HERO ───────────────────────────────────────────── -->
            <div ref="heroRef" class="relative bg-gradient-to-br from-[#00071a] via-[#000d2e] to-black">
                <div class="absolute inset-0 bg-[radial-gradient(ellipse_100%_80%_at_50%_100%,rgba(37,99,235,0.2),transparent)]"></div>
                <div class="absolute inset-0 opacity-[0.03]" style="background-image:radial-gradient(circle,#fff 1px,transparent 1px);background-size:24px 24px;"></div>

                <div class="relative z-10 px-4 md:px-8 lg:px-16 max-w-[1400px] mx-auto pt-8 pb-6">

                    <!-- Breadcrumb -->
                    <div class="flex items-center gap-2 text-xs text-white/30 mb-6">
                        <a :href="route(`${routePrefix}.profile.my`, { slug: profile.slug })" class="hover:text-white/60 transition-colors">
                            {{ profile.name }}
                        </a>
                        <span>/</span>
                        <span class="text-white/50">Studio Pitches</span>
                    </div>

                    <!-- Avatar + identity row -->
                    <div class="flex items-center gap-4 md:gap-5 mb-6">
                        <div class="w-20 h-20 md:w-28 md:h-28 rounded-full bg-gradient-to-br from-purple-800 to-[#000d2e] border-4 border-black flex items-center justify-center shrink-0 shadow-xl ring-1 ring-white/10">
                            <span class="text-white text-2xl md:text-4xl font-bold select-none leading-none">
                                {{ profile.name?.charAt(0).toUpperCase() ?? '?' }}
                            </span>
                        </div>

                        <div class="min-w-0 flex-1">
                            <div class="flex items-center gap-2 flex-wrap">
                                <h1 class="text-white text-xl md:text-2xl font-bold tracking-tight leading-tight">
                                    {{ profile.name }}
                                </h1>
                                <span class="flex items-center gap-1 bg-purple-500/10 border border-purple-500/30 text-purple-400 text-[11px] font-bold px-2 py-0.5 rounded-full shrink-0">
                                    <Clapperboard class="w-3 h-3 shrink-0" />
                                    Pitch Engagement
                                </span>
                            </div>
                            <p class="text-white/50 text-xs mt-1 tracking-wide">Member since {{ profile.memberSince }}</p>
                        </div>

                        <button
                            @click="openShare"
                            class="shrink-0 flex items-center gap-2 px-4 py-2 rounded-full bg-white/15 hover:bg-white/25 backdrop-blur-sm border border-white/25 hover:border-white/40 text-white text-sm font-semibold transition-all"
                        >
                            <Share2 class="w-4 h-4" />
                            Share
                        </button>
                    </div>

                    <!-- Stats grid -->
                    <div class="grid grid-cols-2 sm:grid-cols-4 divide-x divide-white/[0.12] bg-white/[0.08] backdrop-blur-md border border-white/[0.14] rounded-2xl overflow-hidden mt-3">

                        <!-- Requested -->
                        <div class="flex flex-col items-center py-4 px-3 gap-1.5">
                            <div class="w-7 h-7 rounded-full bg-purple-500/10 border border-purple-500/20 flex items-center justify-center">
                                <Clapperboard class="text-purple-400 w-3.5 h-3.5" />
                            </div>
                            <span class="text-white text-xl md:text-2xl font-bold tabular-nums leading-none">{{ formatNumber(requestedCastings.length) }}</span>
                            <span class="text-white/55 text-[10px] uppercase tracking-wider">Requested</span>
                        </div>

                        <!-- Stars Received -->
                        <div class="flex flex-col items-center py-4 px-3 gap-1.5">
                            <div class="w-7 h-7 rounded-full bg-amber-500/10 border border-amber-500/20 flex items-center justify-center">
                                <img :src="STAR_ICON_URL" alt="star" class="w-3.5 h-3.5" />
                            </div>
                            <span class="text-white text-xl md:text-2xl font-bold tabular-nums leading-none">{{ formatNumber(totalStarsReceivedOnRequests) }}</span>
                            <span class="text-white/55 text-[10px] uppercase tracking-wider">Stars Received</span>
                        </div>

                        <!-- Castings Backed -->
                        <div class="flex flex-col items-center py-4 px-3 gap-1.5 border-t sm:border-t-0 border-white/10">
                            <div class="w-7 h-7 rounded-full bg-blue-500/10 border border-blue-500/20 flex items-center justify-center">
                                <Users class="text-blue-400 w-3.5 h-3.5" />
                            </div>
                            <span class="text-white text-xl md:text-2xl font-bold tabular-nums leading-none">{{ formatNumber(supportedCastings.length) }}</span>
                            <span class="text-white/55 text-[10px] uppercase tracking-wider">Pitches Backed</span>
                        </div>

                        <!-- Stars Given -->
                        <div class="flex flex-col items-center py-4 px-3 gap-1.5 border-t sm:border-t-0 border-white/10">
                            <div class="w-7 h-7 rounded-full bg-amber-500/10 border border-amber-500/20 flex items-center justify-center">
                                <img :src="STAR_ICON_URL" alt="star" class="w-3.5 h-3.5" />
                            </div>
                            <span class="text-white text-xl md:text-2xl font-bold tabular-nums leading-none">{{ formatNumber(totalStarsGivenToCastings) }}</span>
                            <span class="text-white/55 text-[10px] uppercase tracking-wider">Stars Given</span>
                        </div>
                    </div>

                </div>
            </div>

            <!-- ── CONTENT ─────────────────────────────────────────── -->
            <div class="px-4 md:px-8 lg:px-16 max-w-[1400px] mx-auto mt-10">
                <div class="grid grid-cols-1 xl:grid-cols-2 gap-10">

                    <!-- ── Casting Requests ───────────────────────── -->
                    <div>
                        <div class="flex items-center gap-3 mb-6">
                            <span class="rounded bg-[#141414]/20 border border-purple-600 px-3 py-1 text-white text-sm font-semibold uppercase tracking-wide shadow">
                                Pitch Requests
                            </span>
                            <span class="text-white/50 text-xs">{{ requestedCastings.length + draftCastings.length }} request{{ requestedCastings.length + draftCastings.length !== 1 ? 's' : '' }}</span>
                        </div>

                        <div v-if="requestedCastings.length || draftCastings.length" class="space-y-3">
                            <CastingCard
                                v-for="casting in draftCastings"
                                :key="`draft-${casting.id}`"
                                :casting="casting"
                                :routePrefix="routePrefix"
                                mode="requested"
                                :requiresAgeVerification="requiresAgeVerification"
                                :isUserLoggedIn="isUserLoggedIn"
                                :userBalance="sharedProps.auth?.user?.wallet_balance || 0"
                                @updateBalance="handleBalanceUpdate"
                                @openBuyStars="isBuyStarsModalOpen = true"
                                @joinClick="handleJoinClick(`CastingsPage_${$event}_Vote_Clicked`, 'general')"
                            />
                            <CastingCard
                                v-for="casting in requestedCastings"
                                :key="casting.id"
                                :casting="casting"
                                :routePrefix="routePrefix"
                                mode="requested"
                                :requiresAgeVerification="requiresAgeVerification"
                                :isUserLoggedIn="isUserLoggedIn"
                                :userBalance="sharedProps.auth?.user?.wallet_balance || 0"
                                @updateBalance="handleBalanceUpdate"
                                @openBuyStars="isBuyStarsModalOpen = true"
                                @joinClick="handleJoinClick(`CastingsPage_${$event}_Vote_Clicked`, 'general')"
                            />
                        </div>

                        <div v-else class="flex flex-col items-center justify-center py-16 text-center">
                            <Clapperboard class="w-10 h-10 text-white/10 mb-3" />
                            <p class="text-white/25 text-sm">No pitch requests yet.</p>
                        </div>
                    </div>

                    <!-- ── Castings Backed ────────────────────────── -->
                    <div>
                        <div class="flex items-center gap-3 mb-6">
                            <span class="rounded bg-[#141414]/20 border border-amber-500 px-3 py-1 text-white text-sm font-semibold uppercase tracking-wide shadow">
                                Pitches Backed
                            </span>
                            <span class="text-white/50 text-xs">{{ supportedCastings.length }} pitches{{ supportedCastings.length !== 1 ? 's' : '' }}</span>
                        </div>

                        <div v-if="supportedCastings.length" class="space-y-3">
                            <CastingCard
                                v-for="casting in supportedCastings"
                                :key="casting.casting_id"
                                :casting="casting"
                                :routePrefix="routePrefix"
                                mode="backed"
                                :requiresAgeVerification="requiresAgeVerification"
                                :isUserLoggedIn="isUserLoggedIn"
                                :userBalance="sharedProps.auth?.user?.wallet_balance || 0"
                                @updateBalance="handleBalanceUpdate"
                                @openBuyStars="isBuyStarsModalOpen = true"
                                @joinClick="handleJoinClick(`CastingsPage_${$event}_Vote_Clicked`, 'general')"
                            />
                        </div>

                        <div v-else class="flex flex-col items-center justify-center py-16 text-center">
                            <div class="w-16 h-16 rounded-full bg-white/5 border border-white/10 flex items-center justify-center mb-3">
                                <img :src="STAR_ICON_URL" alt="" class="w-8 h-8 opacity-20" />
                            </div>
                            <p class="text-white/25 text-sm">No pitches backed yet.</p>
                        </div>
                    </div>

                </div>
            </div>

            <!-- ── FOOTER ─────────────────────────────────────────── -->
            <hr class="border border-[#1a1a1a] mt-20">
            <footer class="mt-8 px-4 md:px-8 lg:px-16 max-w-[1400px] mx-auto">
                <div class="grid grid-cols-1 md:grid-cols-3 gap-8 pb-8">
                    <div>
                        <h3 class="text-white/40 text-xs font-semibold mb-4 uppercase tracking-wider">About</h3>
                        <p class="text-white/25 text-xs leading-relaxed">
                            With a history of over 25 years, Naughty America is recognized as one of the most
                            award-winning adult entertainment platforms online.
                        </p>
                    </div>
                    <div>
                        <h3 class="text-white/40 text-xs font-semibold mb-4 uppercase tracking-wider">Explore</h3>
                        <div class="grid grid-cols-2 gap-y-2 text-xs">
                            <a :href="route('www.main.home')" class="text-white/25 hover:text-white/60 transition-colors">Home</a>
                            <a :href="route('www.scene.index')" class="text-white/25 hover:text-white/60 transition-colors">Videos</a>
                            <a :href="route('www.join.signUp')" class="text-white/25 hover:text-white/60 transition-colors">Join</a>
                            <a :href="route('www.fantasy.index')" class="text-white/25 hover:text-white/60 transition-colors">Categories</a>
                            <a :href="route('www.performer.index', 'top-rated')" class="text-white/25 hover:text-white/60 transition-colors">Pornstars</a>
                            <a :href="route('www.site.index')" class="text-white/25 hover:text-white/60 transition-colors">Sites</a>
                        </div>
                    </div>
                    <div>
                        <h3 class="text-white/40 text-xs font-semibold mb-4 uppercase tracking-wider">Legal</h3>
                        <div class="space-y-2 text-xs">
                            <a :href="route('www.main.privacyPolicy')" class="block text-white/25 hover:text-white/60 transition-colors">Privacy Policy</a>
                            <a :href="route('www.main.termsOfService')" class="block text-white/25 hover:text-white/60 transition-colors">Terms of Service</a>
                            <a :href="route('www.main.usc2257')" class="block text-white/25 hover:text-white/60 transition-colors">18 USC 2257</a>
                        </div>
                    </div>
                </div>
                <div class="border-t border-white/5 py-6 flex items-center justify-between text-[11px] text-white/15">
                    <span>&copy; {{ new Date().getFullYear() }} La Touraine, Inc.</span>
                    <a class="rta-label" href="http://www.rtalabel.org/" target="_blank" rel="noopener">
                        <img width="60" height="28" src="https://sm.naughtycdn.com/images/members2/rta-light.png" alt="RTA" />
                    </a>
                </div>
            </footer>

            <!-- ── GUEST STICKY BOTTOM BAR ────────────────────────── -->
            <Transition
                enter-active-class="transition-transform duration-300 ease-out"
                enter-from-class="translate-y-full"
                enter-to-class="translate-y-0"
                leave-active-class="transition-transform duration-200 ease-in"
                leave-from-class="translate-y-0"
                leave-to-class="translate-y-full"
            >
                <div
                    v-if="!isUserLoggedIn && showGuestCta"
                    class="fixed bottom-0 left-0 right-0 z-50 bg-zinc-950/95 backdrop-blur-xl border-t border-white/10"
                >
                    <div class="px-4 py-3 pb-[calc(env(safe-area-inset-bottom)+0.75rem)] max-w-[1400px] mx-auto flex items-center gap-3">
                        <div class="flex-1 min-w-0">
                            <p class="text-white text-sm font-semibold leading-tight truncate">
                                Build a profile like {{ profile.name }}'s
                            </p>
                            <p class="text-white/40 text-xs mt-0.5">
                                Support pitches and help bring scenes to life.
                            </p>
                        </div>
                        <div class="flex items-center gap-2 shrink-0">
                            <button
                                @click="handleJoinClick('CastingsPage Sticky Bottom Join Clicked')"
                                type="button"
                                class="h-9 px-4 rounded-xl bg-blue-600 hover:bg-blue-500 active:scale-[0.98] text-white text-xs font-bold transition-all shadow-lg shadow-blue-600/20 flex items-center justify-center"
                            >
                                Sign Up
                            </button>
                            <a
                                :href="route('members2.auth.login')"
                                class="h-9 px-4 rounded-xl bg-white/[0.08] hover:bg-white/[0.12] border border-white/10 text-white/70 hover:text-white text-xs font-semibold transition-all flex items-center justify-center"
                            >
                                Sign In
                            </a>
                        </div>
                    </div>
                </div>
            </Transition>

            <ProducerJoinModal :isOpen="isProducerJoinModalOpen"
                               :context="producerJoinModalContext"
                               :source="joinSfwSource || joinDefaultSource"
                               :sfwTrigger="requiresAgeVerification"
                               :ageVerifyUrl="route('na.ageVerify.main.home')"
                               @close="handleProducerJoinModalClose"
            />

            <BuyStarsModal
                :isOpen="isBuyStarsModalOpen"
                :userBalance="sharedProps.auth?.user?.wallet_balance || 0"
                :starPacks="props.starPacks"
                @close="isBuyStarsModalOpen = false"
                @buy="handleBuyStarPack"
            />

            <SharePopup />

        </div>
    </div>
</template>

<style>
.my-page-body #desktop-header { display: none; }
.my-page-body #main { margin-top: 0 !important; margin-bottom: 0; overflow: visible !important; }
.my-page-body #masterlayout { overflow: visible !important; padding-bottom: 0 !important; }
.my-page-body #nprogress .bar { display: none; }
</style>

<style scoped>
.line-clamp-2 {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}
</style>
